import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWatch } from "react-hook-form";

import { i18, trb } from "@utilities";
import {
  DateFormField,
  InputFormField,
  SelectFormField
} from "@components/FormField/HookFormFields";
import {
  selectAvailableCountryOptions,
  selectLegalFirmsOptions,
  selectBasicBusinessOptionsGroups,
  selectBasicBusinessSubGroupOptions,
  selectSectorsOptions,
  selectInstitutions,
  KYCActions,
  selectIsLoadingInstitutions
} from "@redux";
import { FullWidthInputWrapper, StepperFormRow } from "@components";

export const ClientProfile = ({ control, companySector, setValue }) => {
  const businessGroup = useWatch({
    control,
    name: "clientBasicBusiness.0.data.clientBasicBusinessMainGroups"
  });
  const businessSubGroup = useWatch({
    control,
    name: "clientBasicBusiness.0.data.clientBasicBusiness"
  });
  const legalStatus = useWatch({ control, name: "clientProfile.0.data.clientLegalStatus" });
  const institution = useWatch({ control, name: "clientProfile.0.data.insSect" });

  const dispatch = useDispatch();

  const legalFirms = useSelector(selectLegalFirmsOptions);
  const sectorOptions = useSelector(selectSectorsOptions);
  const businessOptionsGroups = useSelector(selectBasicBusinessOptionsGroups);
  const availableCountries = useSelector(selectAvailableCountryOptions);
  const institutions = useSelector(selectInstitutions);
  const isLoading = useSelector(selectIsLoadingInstitutions);
  const subGroups = useSelector(state => selectBasicBusinessSubGroupOptions(state, businessGroup));

  const isInstitutionSelectDisabled =
    isLoading || companySector || (!legalStatus && !businessGroup && !businessSubGroup);

  useEffect(() => {
    if (companySector) return;
    if (legalStatus && businessSubGroup) {
      setValue("clientProfile.0.data.insSect", "");
      dispatch(KYCActions.getCalculatedInstitution(legalStatus, businessSubGroup));
    }
  }, [businessSubGroup, legalStatus, companySector]);

  useEffect(() => {
    if (institutions.length !== 1) return;
    setValue("clientProfile.0.data.insSect", institutions[0].value);
  }, [institutions]);

  return (
    <StepperFormRow>
      <FullWidthInputWrapper>
        <p className="form-group-title">{trb(i18.Labels.ClientProfile)}</p>
      </FullWidthInputWrapper>
      <FullWidthInputWrapper>
        <InputFormField
          control={control}
          name="clientProfile.0.data.clientId"
          title={trb(i18.Labels.ClientId)}
          disabled
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <InputFormField
          control={control}
          name="clientProfile.0.data.clientTitle"
          title={trb(i18.Labels.ClientTitle)}
          disabled
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <InputFormField
          control={control}
          name="clientProfile.0.data.brandName"
          title={trb(i18.Labels.BrandName)}
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <InputFormField
          control={control}
          name="clientProfile.0.data.clientWeb"
          title={trb(i18.Labels.ClientWeb)}
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <SelectFormField
          control={control}
          name="clientProfile.0.data.clientLegalStatus"
          title={trb(i18.Labels.ClientLegalStatus)}
          options={legalFirms}
          optionsLabel="companyDescription"
          optionsValue="companyCode"
          isSearchable
          onChange={() => {
            if (!companySector && institution) setValue("clientProfile.0.data.insSect", "");
          }}
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <SelectFormField
          control={control}
          name="clientBasicBusiness.0.data.clientBasicBusinessMainGroups"
          title={trb(i18.Labels.ClientBasicBusinessGroup)}
          options={businessOptionsGroups}
          onChange={() => {
            setValue("clientBasicBusiness.0.data.clientBasicBusiness", "");
            if (!companySector && institution) {
              setValue("clientProfile.0.data.insSect", "");
            }
          }}
          optionsLabel="label"
          optionsValue="value"
          isSearchable
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <SelectFormField
          control={control}
          name="clientBasicBusiness.0.data.clientBasicBusiness"
          title={trb(i18.Labels.ClientBasicBusinessSubGroups)}
          options={subGroups}
          optionsLabel="label"
          optionsValue="value"
          isSearchable
          disabled={!businessGroup}
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <SelectFormField
          control={control}
          name="clientProfile.0.data.insSect"
          title={trb(i18.Labels.InsSect)}
          options={institutions.length !== 0 ? institutions : sectorOptions}
          optionsLabel="label"
          optionsValue="value"
          isSearchable
          disabled={isInstitutionSelectDisabled}
          loading={isLoading}
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <DateFormField
          control={control}
          name="clientProfile.0.data.clientBirthDate"
          title={trb(i18.Labels.ClientBirthDate)}
          maxDate={new Date()}
        />
      </FullWidthInputWrapper>

      <FullWidthInputWrapper>
        <SelectFormField
          control={control}
          name="clientProfile.0.data.clientRegCountry"
          title={trb(i18.Labels.ClientRegCountry)}
          options={availableCountries}
          optionsLabel="label"
          optionsValue="value"
          isSearchable
          defaultValue="LT"
        />
      </FullWidthInputWrapper>
    </StepperFormRow>
  );
};
